import { useMemoizedFn } from 'ahooks';
import { type RefObject, useEffect, useRef, useState } from 'react';
import type { IPdfImages } from '../types';
import { RENDER_TYPE } from '../types';
import { perfEnd, perfStart } from '../utils/perfMetrics';
import { type TileInfo, type TileLayout, useCanvasRenderer } from './useCanvasRenderer';
import { useImageRenderer } from './useImageRenderer';
import { useSvgRenderer } from './useSvgRenderer';

export type { TileInfo, TileLayout };

/** Canvas → SVG → Image の降级顺序 */
function getNextFallback(type: RENDER_TYPE): RENDER_TYPE | null {
    if (type === RENDER_TYPE.CANVAS) return RENDER_TYPE.SVG;
    if (type === RENDER_TYPE.SVG) return RENDER_TYPE.IMAGE;
    return null;
}

/** Canvas 渲染超时阈值（ms） */
const CANVAS_RENDER_TIMEOUT_MS = 15_000;

/**
 * PDF 页面渲染 Hook 的 Props 接口
 */
interface UsePdfRendererProps {
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
    pdfDoc: any;
    pageNum: number;
    scale: number;
    screenState?: 'no-full' | 'full';
    renderType: RENDER_TYPE;
    renderPixelRadio?: number;
    canvasRef: RefObject<HTMLCanvasElement>;
    svgContainerRef?: RefObject<HTMLDivElement>;
    annotationLayerRef?: RefObject<HTMLDivElement>;
    pdfImages?: IPdfImages;
    visible?: boolean;
    onRenderSuccess?: () => void;
    // biome-ignore lint/suspicious/noExplicitAny: error type
    onRenderError?: (error: any) => void;
    onInternalLinkClick?: (pageNum: number) => void;
    /** 自动降级发生时触发，参数为实际切换到的渲染模式 */
    onRenderFallback?: (fallbackType: RENDER_TYPE) => void;
    enableCjkFix?: boolean;
}

/**
 * PDF 页面渲染 Hook（编排层）
 *
 * 根据 renderType 将渲染委托给：
 * - useCanvasRenderer：Canvas + Tile 分块路径（含 iOS DPR 降级）
 * - useSvgRenderer：SVG 路径（失败时回退 Canvas）
 * - useImageRenderer：Image 路径（含预加载队列）
 *
 * 三级自动降级链：Canvas OOM/超时 → SVG → Image
 */
export function usePdfRenderer(props: UsePdfRendererProps) {
    const {
        pdfDoc,
        pageNum,
        scale,
        screenState,
        renderType,
        renderPixelRadio,
        canvasRef,
        svgContainerRef,
        annotationLayerRef,
        pdfImages,
        visible = true,
        onRenderSuccess,
        onRenderError,
        onInternalLinkClick,
        onRenderFallback,
        enableCjkFix,
    } = props;

    // biome-ignore lint/suspicious/noExplicitAny: error type
    const [error, setError] = useState<any>(null);
    const renderIdRef = useRef(0);

    // ===== 三级降级状态 =====
    const [effectiveRenderType, setEffectiveRenderType] = useState<RENDER_TYPE>(renderType);

    // 外部显式切换 renderType 时重置降级状态
    useEffect(() => {
        setEffectiveRenderType(renderType);
    }, [renderType]);

    // ===== TTFPR（首页渲染时间）追踪 =====
    const ttfprStartRef = useRef<number>(0);
    const firstPageDoneRef = useRef(false);

    // 重置 TTFPR 计时（换文档时）
    useEffect(() => {
        firstPageDoneRef.current = false;
        ttfprStartRef.current = 0;
    }, [pdfDoc]);

    // ===== 渲染成功回调（含 TTFPR 采集）=====
    const wrappedOnRenderSuccess = useMemoizedFn(() => {
        onRenderSuccess?.();
        if (pageNum === 1 && !firstPageDoneRef.current && ttfprStartRef.current > 0) {
            firstPageDoneRef.current = true;
            const duration = performance.now() - ttfprStartRef.current;
            perfEnd('ttfpr', 'page1', { renderType: effectiveRenderType, scale });
            if (typeof window !== 'undefined') {
                try {
                    window.dispatchEvent(
                        new CustomEvent('pdf:first-page-render-end', {
                            detail: { duration, renderType: effectiveRenderType },
                        })
                    );
                } catch {
                    // ignore
                }
            }
        }
    });

    // ===== 降级错误处理（从 render 内部提取以降低认知复杂度）=====
    const handleRenderCatchError = useMemoizedFn((err: unknown) => {
        const next = getNextFallback(effectiveRenderType);
        if (next !== null) {
            setEffectiveRenderType(next);
            onRenderFallback?.(next);
        } else {
            setError(err);
            onRenderError?.(err);
        }
    });

    // ===== SVG 完全失败时触发 Image 降级 =====
    const handleSvgFailedCompletely = useMemoizedFn(() => {
        if (effectiveRenderType !== RENDER_TYPE.IMAGE) {
            setEffectiveRenderType(RENDER_TYPE.IMAGE);
            onRenderFallback?.(RENDER_TYPE.IMAGE);
        }
    });

    const sharedProps = {
        scale,
        pageNum,
        screenState,
        renderPixelRadio,
        canvasRef,
        annotationLayerRef,
        visible,
        pdfDoc,
        onRenderSuccess: wrappedOnRenderSuccess,
        onRenderError,
        onInternalLinkClick,
        enableCjkFix,
    };

    // tile 渲染错误需走降级链（Canvas → SVG → Image），而非静默吞掉（外部 onRenderError 在 PdfPage 中未传入）
    const { renderByCanvas, renderCanvas, tileLayout, registerTileCanvas, cancelPending, resetForNewPage } =
        useCanvasRenderer({ ...sharedProps, renderType: effectiveRenderType, onRenderError: handleRenderCatchError });

    const { renderBySvg } = useSvgRenderer({
        scale,
        pageNum,
        canvasRef,
        svgContainerRef,
        annotationLayerRef,
        onRenderSuccess: wrappedOnRenderSuccess,
        onRenderError,
        renderByCanvas,
        pdfDoc,
        onInternalLinkClick,
        onSvgFailedCompletely: effectiveRenderType === RENDER_TYPE.SVG ? handleSvgFailedCompletely : undefined,
    });

    const { renderByImage, isImageRenderDisabledRef } = useImageRenderer({
        pageNum,
        scale,
        renderPixelRadio,
        canvasRef,
        annotationLayerRef,
        pdfImages,
        onRenderSuccess: wrappedOnRenderSuccess,
        onRenderError,
        pdfDoc,
        onInternalLinkClick,
    });

    // ===== 主渲染 Effect =====

    // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
    const dispatchImageRender = async (page: any, isStale: () => boolean) => {
        if (isImageRenderDisabledRef.current) {
            await renderByCanvas(page);
            return;
        }
        try {
            let imageCancelled = false;
            const isStaleForImage = () => imageCancelled || isStale();
            await Promise.race([
                renderByImage(page, isStaleForImage),
                new Promise<void>((_resolve, reject) => {
                    setTimeout(() => {
                        imageCancelled = true;
                        reject(new Error(`IMAGE render timeout after ${CANVAS_RENDER_TIMEOUT_MS}ms`));
                    }, 20_000);
                }),
            ]);
        } catch (_imageErr: unknown) {
            isImageRenderDisabledRef.current = true;
            await renderByCanvas(page);
        }
    };

    // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
    const dispatchRender = async (page: any, isStale: () => boolean, canvasEl: HTMLElement | null) => {
        if (canvasEl) canvasEl.style.display = 'block';
        if (effectiveRenderType === RENDER_TYPE.CANVAS) {
            await Promise.race([
                renderCanvas(page),
                new Promise<never>((_resolve, reject) =>
                    setTimeout(
                        () => reject(new Error(`Canvas render timeout after ${CANVAS_RENDER_TIMEOUT_MS}ms`)),
                        CANVAS_RENDER_TIMEOUT_MS
                    )
                ),
            ]);
        } else if (effectiveRenderType === RENDER_TYPE.SVG) {
            renderBySvg(page, isStale);
        } else if (effectiveRenderType === RENDER_TYPE.IMAGE) {
            await dispatchImageRender(page, isStale);
        } else {
            await renderByCanvas(page);
        }
    };

    useEffect(() => {
        if (!visible) return;
        if (!pdfDoc) return;
        if (!pageNum) return;
        if (!scale || scale <= 0 || Number.isNaN(scale)) return;

        // TTFPR 计时起点：首页首次渲染时
        if (pageNum === 1 && !firstPageDoneRef.current && ttfprStartRef.current === 0) {
            ttfprStartRef.current = performance.now();
            perfStart('ttfpr', 'page1', { renderType: effectiveRenderType, scale });
        }

        const renderId = ++renderIdRef.current;
        let isCancelled = false;
        const isStale = () => isCancelled || renderIdRef.current !== renderId;

        const render = async () => {
            setError(null);

            const canvasEl = canvasRef.current;
            const prevVisibility = canvasEl?.style.visibility;
            if (canvasEl && effectiveRenderType === RENDER_TYPE.IMAGE) {
                canvasEl.style.visibility = 'hidden';
            }

            try {
                resetForNewPage(pageNum);
                // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
                const page: any = await pdfDoc.getPage(pageNum);
                if (isStale()) return;
                await dispatchRender(page, isStale, canvasEl ?? null);
            } catch (err: unknown) {
                if (!isStale()) {
                    handleRenderCatchError(err);
                }
            } finally {
                if (canvasEl && effectiveRenderType === RENDER_TYPE.IMAGE) {
                    canvasEl.style.visibility = prevVisibility || 'visible';
                }
            }
        };

        render();

        return () => {
            isCancelled = true;
            cancelPending();
            const canvasEl = canvasRef.current;
            if (canvasEl && effectiveRenderType === RENDER_TYPE.IMAGE) {
                canvasEl.style.visibility = 'visible';
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfDoc, pageNum, scale, screenState, effectiveRenderType, visible]);

    return { error, tileLayout, registerTileCanvas };
}
